import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { saveAdminUserProfile, useAdminUserProfileQuery, useAdminUserQuery } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';
import AccountStatusChip, { accountSummary } from './AccountStatusChip';
import { PanelField as Field } from './OverviewPanel';
import ProductChips from './ProductChips';

interface FormState {
	coverage_focus: string;
	location: string;
	notes: string;
}

interface Props {
	userId: number;
}

/**
 * The info side of the user page's header card: which products the user has (read-only),
 * their account, and the internal profile (crm.user_profile) with an inline edit.
 */
export default function UserProfileHeader({ userId }: Props) {
	const queryClient = useQueryClient();
	const { data: profile, isLoading } = useAdminUserProfileQuery(userId);
	const { data: user } = useAdminUserQuery(userId);

	const [editing, setEditing] = useState(false);
	const [form, setForm] = useState<FormState>({ coverage_focus: '', location: '', notes: '' });
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function startEdit() {
		setForm({
			coverage_focus: profile?.coverage_focus ?? '',
			location: profile?.location ?? '',
			notes: profile?.notes ?? '',
		});
		setError(null);
		setEditing(true);
	}

	async function handleSave() {
		setSaving(true);
		setError(null);
		try {
			await saveAdminUserProfile(userId, {
				coverage_focus: form.coverage_focus.trim() || null,
				location: form.location.trim() || null,
				notes: form.notes.trim() || null,
			});
			await queryClient.invalidateQueries(['admin', 'users', userId, 'profile']);
			setEditing(false);
		} catch (e: any) {
			setError(apiErrorMessage(e, 'Failed to save the profile.'));
		} finally {
			setSaving(false);
		}
	}

	if (editing) {
		return (
			<Stack spacing={1.5}>
				{error && <Alert severity="error">{error}</Alert>}
				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
					<TextField
						label="Coverage focus"
						size="small"
						placeholder="Sector / industry focus"
						value={form.coverage_focus}
						onChange={(e) => setForm({ ...form, coverage_focus: e.target.value })}
						sx={{ flex: 1, minWidth: 200 }}
					/>
					<TextField
						label="Location"
						size="small"
						value={form.location}
						onChange={(e) => setForm({ ...form, location: e.target.value })}
						sx={{ flex: 1, minWidth: 200 }}
					/>
				</Box>
				<TextField
					label="Notes"
					size="small"
					multiline
					minRows={3}
					value={form.notes}
					onChange={(e) => setForm({ ...form, notes: e.target.value })}
				/>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
					<Button onClick={() => setEditing(false)} disabled={saving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSave}
						disabled={saving}
						startIcon={saving ? <CircularProgress size={16} /> : undefined}
					>
						{saving ? 'Saving…' : 'Save'}
					</Button>
				</Box>
			</Stack>
		);
	}

	return (
		<Stack spacing={1.5} sx={{ minWidth: 0 }}>
			{error && <Alert severity="error">{error}</Alert>}
			<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
				<Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', columnGap: 4, rowGap: 1.5, alignItems: 'flex-start' }}>
					<Field label="Products">
						<ProductChips user={user} />
					</Field>
					<Field label="Account">
						{user ? (
							<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
								<AccountStatusChip account={user.account} isVendor={user.is_vendor} />
								{user.account && (
									<Typography variant="caption" color="text.secondary">
										<Link
											component={RouterLink}
											to={`/admin/accounts/${user.account.account_id}`}
											state={{ accountName: user.account.account_name }}
										>
											{user.account.account_name}
										</Link>{' '}
										· {accountSummary(user.account)}
									</Typography>
								)}
							</Stack>
						) : (
							<Skeleton width={160} height={28} />
						)}
					</Field>
					<Field label="Coverage focus">
						{isLoading ? (
							<Skeleton width={80} />
						) : profile?.coverage_focus ? (
							<Typography variant="body2">{profile.coverage_focus}</Typography>
						) : null}
					</Field>
					<Field label="Location">
						{isLoading ? (
							<Skeleton width={80} />
						) : profile?.location ? (
							<Typography variant="body2">{profile.location}</Typography>
						) : null}
					</Field>
				</Box>
				<Button size="small" startIcon={<EditIcon />} onClick={startEdit} disabled={isLoading} sx={{ flexShrink: 0 }}>
					Edit
				</Button>
			</Box>
			{profile?.notes && (
				<Field label="Notes">
					<Tooltip
						title={<span style={{ whiteSpace: 'pre-wrap' }}>{profile.notes}</span>}
						placement="bottom-start"
						enterDelay={400}
					>
						<Typography
							variant="body2"
							sx={{
								whiteSpace: 'pre-wrap',
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
							}}
						>
							{profile.notes}
						</Typography>
					</Tooltip>
				</Field>
			)}
		</Stack>
	);
}
